// Vertical light columns — glowing cyan pillars that spawn at grid intersections,
// fade in, hold, optional phaser bolt with impact sparks, then fade out.

import {
  Mesh,
  BoxGeometry,
  Group,
  AdditiveBlending,
} from 'three/webgpu';
import { MeshBasicNodeMaterial } from 'three/webgpu';
import { vec3, uniform } from 'three/tsl';
import type UniformNode from 'three/src/nodes/core/UniformNode.js';

const MAX_COLUMNS = 20;
const COLUMN_HEIGHT = 160;
const COLUMN_WIDTH = 0.08;
const FADE_OUT_MAX_SCALE = 50.0;
const FADE_IN_DURATION = 1.5;
const HOLD_MIN = 1.0;
const HOLD_MAX = 3.0;
const FADE_OUT_DURATION = 0.18;
const MAX_OPACITY = 0.7;
const SPAWN_INTERVAL_MIN = 0.3;
const SPAWN_INTERVAL_MAX = 0.8;
const GRID_HALF_EXTENT = 35;
const GRID_SCROLL_SPEED = 2.0;

// Phaser bolt settings
const PHASER_CHANCE = 0.33;
const PHASER_DURATION = 1.2;
const BOLT_HEIGHT = 15.0;
const BOLT_WIDTH = COLUMN_WIDTH;

// Spark settings
const SPARKS_PER_IMPACT = 12;
const SPARK_POOL_SIZE = 80;
const SPARK_LIFETIME = 0.6;
const SPARK_SIZE = 0.12;
const SPARK_SPEED_MIN = 8.0;
const SPARK_SPEED_MAX = 18.0;
const SPARK_GRAVITY = -30.0;

type Phase = 'idle' | 'fade_in' | 'hold' | 'phaser' | 'fade_out';

interface ColumnState {
  phase: Phase;
  startTime: number;
  holdDuration: number;
  phaserStartTime: number;
  hasPhaser: boolean;
  opacity: number;
  initialZ: number;
  mesh: Mesh;
  boltMesh: Mesh;
  uOpacity: UniformNode<number>;
  uBoltOpacity: UniformNode<number>;
}

interface SparkState {
  active: boolean;
  birthTime: number;
  // Initial position
  x0: number;
  y0: number;
  z0: number;
  // Initial velocity
  vx: number;
  vy: number;
  vz: number;
  mesh: Mesh;
  uOpacity: UniformNode<number>;
}

export interface LightColumns {
  group: Group;
  update: (elapsed: number) => void;
  dispose: () => void;
}

export function createLightColumns(onFadeOut?: (x: number, z: number, time: number) => void): LightColumns {
  const group = new Group();

  // Shared geometries
  const columnGeometry = new BoxGeometry(COLUMN_WIDTH, COLUMN_HEIGHT, COLUMN_WIDTH);
  const boltGeometry = new BoxGeometry(BOLT_WIDTH, BOLT_HEIGHT, BOLT_WIDTH);
  const sparkGeometry = new BoxGeometry(SPARK_SIZE, SPARK_SIZE, SPARK_SIZE);

  // --- Spark pool ---
  const sparks: SparkState[] = [];
  let sparkIndex = 0;

  for (let i = 0; i < SPARK_POOL_SIZE; i++) {
    const uSparkOpacity = uniform(0.0);
    const sparkMaterial = new MeshBasicNodeMaterial();
    // HDR amber-white for bright bloom
    sparkMaterial.colorNode = vec3(3.0, 2.0, 0.5);
    sparkMaterial.opacityNode = uSparkOpacity;
    sparkMaterial.transparent = true;
    sparkMaterial.depthWrite = false;
    sparkMaterial.blending = AdditiveBlending;

    const sparkMesh = new Mesh(sparkGeometry, sparkMaterial);
    sparkMesh.visible = false;
    group.add(sparkMesh);

    sparks.push({
      active: false,
      birthTime: 0,
      x0: 0, y0: 0, z0: 0,
      vx: 0, vy: 0, vz: 0,
      mesh: sparkMesh,
      uOpacity: uSparkOpacity,
    });
  }

  function emitSparks(x: number, y: number, z: number, elapsed: number): void {
    for (let s = 0; s < SPARKS_PER_IMPACT; s++) {
      const spark = sparks[sparkIndex];
      sparkIndex = (sparkIndex + 1) % SPARK_POOL_SIZE;

      spark.active = true;
      spark.birthTime = elapsed;
      spark.x0 = x;
      spark.y0 = y;
      spark.z0 = z;

      // Random velocity in upward cone with outward spread
      const angle = Math.random() * Math.PI * 2;
      const speed = SPARK_SPEED_MIN + Math.random() * (SPARK_SPEED_MAX - SPARK_SPEED_MIN);
      const upRatio = 0.5 + Math.random() * 0.5;  // mostly upward
      spark.vx = Math.cos(angle) * speed * (1 - upRatio);
      spark.vy = speed * upRatio;
      spark.vz = Math.sin(angle) * speed * (1 - upRatio);

      spark.mesh.visible = true;
    }
  }

  function updateSparks(elapsed: number): void {
    for (let i = 0; i < SPARK_POOL_SIZE; i++) {
      const spark = sparks[i];
      if (!spark.active) continue;

      const age = elapsed - spark.birthTime;
      if (age >= SPARK_LIFETIME) {
        spark.active = false;
        spark.mesh.visible = false;
        spark.uOpacity.value = 0;
        continue;
      }

      // Physics: position = p0 + v*t + 0.5*g*t^2
      const posY = spark.y0 + spark.vy * age + 0.5 * SPARK_GRAVITY * age * age;
      // Clamp to grid level (y=0)
      spark.mesh.position.x = spark.x0 + spark.vx * age;
      spark.mesh.position.y = Math.max(0, posY);
      spark.mesh.position.z = spark.z0 + spark.vz * age;

      // Fade out over lifetime
      const life = 1 - age / SPARK_LIFETIME;
      spark.uOpacity.value = life * life;  // quadratic fade

      // Kill if fallen back to grid
      if (posY < 0 && age > 0.1) {
        spark.active = false;
        spark.mesh.visible = false;
        spark.uOpacity.value = 0;
      }
    }
  }

  // --- Columns ---
  const columns: ColumnState[] = [];

  for (let i = 0; i < MAX_COLUMNS; i++) {
    const uOpacity = uniform(0.0);

    const material = new MeshBasicNodeMaterial();
    material.colorNode = vec3(0.0, 0.898, 1.0);
    material.opacityNode = uOpacity;
    material.transparent = true;
    material.depthWrite = false;
    material.blending = AdditiveBlending;

    const mesh = new Mesh(columnGeometry, material);
    mesh.position.set(0, COLUMN_HEIGHT / 2, 0);
    mesh.visible = false;
    group.add(mesh);

    // Bolt mesh — bright energy projectile
    const uBoltOpacity = uniform(0.0);
    const boltMaterial = new MeshBasicNodeMaterial();
    boltMaterial.colorNode = vec3(5.0, 5.0, 5.0);
    boltMaterial.opacityNode = uBoltOpacity;
    boltMaterial.transparent = true;
    boltMaterial.depthWrite = false;
    boltMaterial.blending = AdditiveBlending;

    const boltMesh = new Mesh(boltGeometry, boltMaterial);
    boltMesh.visible = false;
    group.add(boltMesh);

    columns.push({
      phase: 'idle',
      startTime: 0,
      holdDuration: 0,
      phaserStartTime: 0,
      hasPhaser: false,
      opacity: 0,
      initialZ: 0,
      mesh,
      boltMesh,
      uOpacity,
      uBoltOpacity,
    });
  }

  let nextSpawnTime = 0.5;
  let spawnIndex = 0;

  function update(elapsed: number): void {
    // Spawn new columns at grid intersections
    if (elapsed >= nextSpawnTime) {
      const col = columns[spawnIndex];
      col.phase = 'fade_in';
      col.startTime = elapsed;
      col.holdDuration = HOLD_MIN + Math.random() * (HOLD_MAX - HOLD_MIN);
      col.hasPhaser = Math.random() < PHASER_CHANCE;
      col.opacity = 0;

      const x = Math.round((Math.random() - 0.5) * 2 * GRID_HALF_EXTENT);
      const scrollFrac = (elapsed * GRID_SCROLL_SPEED) % 1.0;
      const baseZ = Math.round((Math.random() - 0.5) * 2 * GRID_HALF_EXTENT);
      const z = baseZ + scrollFrac;
      col.initialZ = z;
      col.mesh.position.x = x;
      col.mesh.position.z = z;
      col.mesh.scale.set(1, 1, 1);
      col.mesh.position.y = COLUMN_HEIGHT / 2;
      col.mesh.visible = true;
      col.boltMesh.visible = false;

      spawnIndex = (spawnIndex + 1) % MAX_COLUMNS;
      nextSpawnTime = elapsed + SPAWN_INTERVAL_MIN + Math.random() * (SPAWN_INTERVAL_MAX - SPAWN_INTERVAL_MIN);
    }

    // Update lifecycle for each column
    for (let i = 0; i < MAX_COLUMNS; i++) {
      const col = columns[i];
      const age = elapsed - col.startTime;

      switch (col.phase) {
        case 'fade_in':
          col.opacity = Math.min(MAX_OPACITY, (age / FADE_IN_DURATION) * MAX_OPACITY);
          if (age >= FADE_IN_DURATION) col.phase = 'hold';
          break;

        case 'hold': {
          const holdAge = age - FADE_IN_DURATION;
          const holdProgress = holdAge / col.holdDuration;
          if (col.hasPhaser) {
            const intensity = holdProgress * holdProgress;
            const minOpacity = MAX_OPACITY * (1 - intensity * 0.95);
            const flicker = (Math.sin(elapsed * 80) + 1) * 0.5;
            col.opacity = minOpacity + (MAX_OPACITY - minOpacity) * flicker;
          } else {
            col.opacity = MAX_OPACITY;
          }
          if (age >= FADE_IN_DURATION + col.holdDuration) {
            if (col.hasPhaser) {
              col.phase = 'phaser';
              col.phaserStartTime = elapsed;
              col.boltMesh.position.x = col.mesh.position.x;
              col.boltMesh.position.z = col.mesh.position.z;
              col.boltMesh.position.y = COLUMN_HEIGHT;
              col.boltMesh.visible = true;
            } else {
              col.phase = 'fade_out';
            }
          }
          break;
        }

        case 'phaser': {
          const phaserAge = elapsed - col.phaserStartTime;
          const progress = Math.min(1, phaserAge / PHASER_DURATION);

          const minBoltY = BOLT_HEIGHT / 2;
          const boltY = COLUMN_HEIGHT - progress * (COLUMN_HEIGHT - minBoltY);
          col.boltMesh.position.y = boltY;
          col.boltMesh.position.z = col.mesh.position.z;

          col.mesh.scale.y = Math.max(0.001, boltY / COLUMN_HEIGHT);
          col.mesh.position.y = boltY / 2;

          col.uBoltOpacity.value = 1.0;

          // Shimmer persists at max intensity during phaser
          const phaserMinOp = MAX_OPACITY * 0.05;
          const phaserBaseOp = MAX_OPACITY * 0.5;
          const phaserFlicker = (Math.sin(elapsed * 80) + 1) * 0.5;
          col.opacity = phaserMinOp + (phaserBaseOp - phaserMinOp) * phaserFlicker;

          if (progress >= 1) {
            col.boltMesh.visible = false;
            col.uBoltOpacity.value = 0;
            // Emit sparks at impact point
            emitSparks(col.mesh.position.x, 0, col.mesh.position.z, elapsed);
            col.phase = 'fade_out';
            if (onFadeOut) onFadeOut(col.mesh.position.x, col.mesh.position.z, elapsed);
          }
          break;
        }

        case 'fade_out': {
          const fadeStart = FADE_IN_DURATION + col.holdDuration
            + (col.hasPhaser ? PHASER_DURATION : 0);
          const fadeAge = age - fadeStart;
          const fadeProgress = Math.min(1, fadeAge / FADE_OUT_DURATION);
          const eased = 1 - (1 - fadeProgress) * (1 - fadeProgress);
          const baseFadeOpacity = Math.max(0, MAX_OPACITY * (1 - eased));
          if (col.hasPhaser) {
            // Shimmer persists during fade_out, modulating the fading opacity
            const fadeFlicker = (Math.sin(elapsed * 80) + 1) * 0.5;
            col.opacity = baseFadeOpacity * fadeFlicker;
            col.mesh.scale.set(1, 1, 1);
            col.mesh.position.y = COLUMN_HEIGHT / 2;
          } else {
            col.opacity = baseFadeOpacity;
            const widthScale = 1 + eased * (FADE_OUT_MAX_SCALE - 1);
            col.mesh.scale.x = widthScale;
            col.mesh.scale.z = widthScale;
          }
          if (fadeAge >= FADE_OUT_DURATION) {
            col.phase = 'idle';
            col.opacity = 0;
            col.mesh.visible = false;
          }
          break;
        }

        default:
          col.opacity = 0;
      }

      col.uOpacity.value = col.opacity;

      // Move column and bolt with the grid scroll
      if (col.phase !== 'idle') {
        const currentZ = col.initialZ + GRID_SCROLL_SPEED * (elapsed - col.startTime);
        col.mesh.position.z = currentZ;
        if (col.boltMesh.visible) {
          col.boltMesh.position.z = currentZ;
        }
      }
    }

    // Update all active sparks
    updateSparks(elapsed);
  }

  function dispose(): void {
    columnGeometry.dispose();
    boltGeometry.dispose();
    sparkGeometry.dispose();
    for (const col of columns) {
      (col.mesh.material as MeshBasicNodeMaterial).dispose();
      (col.boltMesh.material as MeshBasicNodeMaterial).dispose();
    }
    for (const spark of sparks) {
      (spark.mesh.material as MeshBasicNodeMaterial).dispose();
    }
  }

  return { group, update, dispose };
}
